import axios, { type InternalAxiosRequestConfig } from 'axios'
import { createCorrelationId } from '@/core/utils/uuid'
import { selectedErrorLocale } from './businessErrors'
import {
  captureRefreshSessionGuard,
  clearSession,
  commitRefreshedSession,
  currentSessionRevision,
  isSessionRevisionCurrent,
  readSession,
  type RefreshSessionGuard,
  type SessionPayload
} from '@/core/auth/session'
import { REAUTH_HEADER, reauthCode, requestReauthPassword } from './reauth'

interface RefreshResponse extends SessionPayload {
  expiresAtUtc: string
}

interface RetriableRequestConfig extends InternalAxiosRequestConfig {
  __retried?: boolean
  __sessionRevision?: number
}

export const http = axios.create({ baseURL: import.meta.env.VITE_API_URL ?? '/api/v1', timeout: 20000 })
const refreshInFlight = new Map<string, Promise<RefreshResponse>>()
const refreshSkewSeconds = 30

export function validTraceparent(value: unknown): value is string {
  if (typeof value !== 'string') return false
  const match = /^([0-9a-f]{2})-([0-9a-f]{32})-([0-9a-f]{16})-([0-9a-f]{2})(?:-[0-9a-f]+)*$/.exec(value)
  return Boolean(match && match[1] !== 'ff' && match[2] !== '0'.repeat(32)
    && match[3] !== '0'.repeat(16) && (match[1] !== '00' || value.length === 55))
}

export function createTraceparent(): string {
  return `00-${createCorrelationId()}-${createCorrelationId().slice(0, 16)}-00`
}

function diagnosticHeaders(parent?: unknown) {
  const traceparent = validTraceparent(parent) ? parent : createTraceparent()
  return { 'X-Correlation-ID': traceparent.split('-')[1]!, traceparent }
}

// Auth lifecycle calls must never enter the protected-request refresh interceptors.
export function revokeRefreshToken(refreshToken: string, accessToken: string | null) {
  return axios.post(`${http.defaults.baseURL}/auth/logout`, { refreshToken }, {
    timeout: 20000,
    headers: { ...diagnosticHeaders(), 'Accept-Language': selectedErrorLocale(), ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) }
  })
}

export async function endSsoSession() {
  try { await axios.delete(`${http.defaults.baseURL}/auth/sso-session`, { timeout: 10000, headers: diagnosticHeaders() }) }
  catch { /* SSO tắt hoặc mạng lỗi: cookie (nếu có) tự hết hạn. */ }
}

export async function logoutSession() {
  const { refreshToken, accessToken } = readSession()
  const pending = refreshToken ? refreshInFlight.get(refreshToken) : undefined
  // Invalidate guards immediately: no new request may refresh the departing session.
  clearSession()
  // ADR-085: xóa luôn phiên SSO (cookie) để hệ thống dùng chung đăng nhập không tự đăng nhập lại; chạy song song với thu hồi token.
  const ssoEnded = endSsoSession()
  try {
    if (!refreshToken) return
    if (pending) {
      try { await pending } catch { /* A rotated response is revoked by refreshSession below. */ }
    }
    await revokeRefreshToken(refreshToken, accessToken)
  } finally { await ssoEnded }
}

class SessionChangedError extends Error {
  readonly code = 'session_changed'
  constructor() {
    super('The authenticated session changed while the request was in progress.')
    this.name = 'SessionChangedError'
  }
}

function expiresSoon(token: string) {
  try {
    const encodedPayload = token.split('.')[1]
    if (!encodedPayload) return false
    const normalized = encodedPayload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
    const payload = JSON.parse(atob(padded)) as { exp?: unknown }
    return typeof payload.exp === 'number'
      && payload.exp <= Math.floor(Date.now() / 1000) + refreshSkewSeconds
  } catch {
    return false
  }
}

http.interceptors.request.use(async config => {
  const diagnostics = diagnosticHeaders(config.headers.get('traceparent'))
  config.headers.set('X-Correlation-ID', diagnostics['X-Correlation-ID'])
  config.headers.set('traceparent', diagnostics.traceparent)
  config.headers['Accept-Language'] = selectedErrorLocale()
  const retriableConfig = config as RetriableRequestConfig
  retriableConfig.__sessionRevision ??= currentSessionRevision()
  if (!isSessionRevisionCurrent(retriableConfig.__sessionRevision)) throw new SessionChangedError()
  if (config.url === '/auth/login' || config.url?.startsWith('/auth/platform-login')) return config
  const session = readSession()
  let token = session.accessToken
  const refreshToken = session.refreshToken
  if (token && refreshToken && expiresSoon(token)) {
    token = (await refreshSession(captureRefreshSessionGuard(refreshToken))).accessToken
  }
  if (!isSessionRevisionCurrent(retriableConfig.__sessionRevision)) throw new SessionChangedError()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

function requestAuthorization(config: RetriableRequestConfig) {
  const value = typeof config.headers.get === 'function'
    ? config.headers.get('Authorization')
    : config.headers.Authorization
  return typeof value === 'string' ? value : ''
}

/**
 * Bỏ phiên hiện tại và đưa người dùng về trang đăng nhập.
 *
 * Phải gọi cả khi 401 mà KHÔNG có refresh token: trước đây nhánh đó chỉ reject lặng lẽ, nên người dùng ngồi lại
 * trên trang với một lỗi mơ hồ ("Unable to load chat.") mà không có gì cho biết phiên đã hết và phải đăng nhập lại.
 */
function endSession(guard?: RefreshSessionGuard) {
  const ended = clearSession(guard)
  if (ended && window.location.pathname !== '/login') window.location.assign('/login')
  return ended
}

function refreshSession(guard: RefreshSessionGuard) {
  let pending = refreshInFlight.get(guard.refreshToken)
  if (!pending) {
    pending = axios.post<RefreshResponse>(`${http.defaults.baseURL}/auth/refresh`, { refreshToken: guard.refreshToken }, { timeout: 20000, headers: { ...diagnosticHeaders(), 'Accept-Language': selectedErrorLocale() } })
      .then(async ({ data }) => {
        if (!commitRefreshedSession(data, guard)) {
          // Logout or a newer login won the race; do not leave the rotated token active.
          try { await revokeRefreshToken(data.refreshToken, data.accessToken) } catch { /* Remote revocation could not be confirmed. */ }
          throw new SessionChangedError()
        }
        return data
      })
      .catch(error => {
        if (error.response?.status === 401 || error.response?.data?.code === 'invalid_refresh_token') endSession(guard)
        throw error
      })
      .finally(() => { refreshInFlight.delete(guard.refreshToken) })
    refreshInFlight.set(guard.refreshToken, pending)
  }
  return pending
}

http.interceptors.response.use(response => {
  const expected = (response.config as RetriableRequestConfig).__sessionRevision
  if (expected !== undefined && !isSessionRevisionCurrent(expected)) throw new SessionChangedError()
  return response
}, async error => {
  const config = error.config as RetriableRequestConfig | undefined
  if (error.response?.status !== 401 || !config || config.__retried) return Promise.reject(error)

  config.__retried = true
  const requestRevision = config.__sessionRevision
  if (requestRevision === undefined || !isSessionRevisionCurrent(requestRevision)) return Promise.reject(error)

  const session = readSession()
  const currentAccessToken = session.accessToken
  const currentAuthorization = currentAccessToken ? `Bearer ${currentAccessToken}` : ''
  if (currentAuthorization && requestAuthorization(config) !== currentAuthorization) {
    config.headers.Authorization = currentAuthorization
    return http.request(config)
  }

  const refreshToken = session.refreshToken
  if (!refreshToken) {
    endSession()
    return Promise.reject(error)
  }
  try {
    const tokens = await refreshSession(captureRefreshSessionGuard(refreshToken))
    if (!isSessionRevisionCurrent(requestRevision)) return Promise.reject(error)
    config.headers.Authorization = `Bearer ${tokens.accessToken}`
    return http.request(config)
  } catch (refreshError) {
    return Promise.reject(refreshError)
  }
})

// ADR-070: đổi quyền cần nhập lại mật khẩu; hỏi tối đa 3 lần rồi gửi lại cùng request với header X-Reauth-Password.
http.interceptors.response.use(undefined, async error => {
  const config = error.config as (RetriableRequestConfig & { __reauthAttempts?: number }) | undefined
  const code = reauthCode(error)
  if (!config || !code || (config.__reauthAttempts ?? 0) >= 3) return Promise.reject(error)
  const password = await requestReauthPassword(code === 'reauth_failed')
  if (!password) return Promise.reject(error)
  config.__reauthAttempts = (config.__reauthAttempts ?? 0) + 1
  config.headers.set(REAUTH_HEADER, password)
  return http.request(config)
})

export interface Paged<T> { items: T[]; page: number; pageSize: number; total: number; totalPages: number }
export { businessErrorMessage, formatBusinessError } from './businessErrors'
